/**
 * computeLedger(state, today) turns the raw state into every number the screens show:
 * item totals and shares, each person's balance, paid/partly/unpaid status per share,
 * receipt checks, warnings, the item catalog and spending breakdowns.
 *
 * It is a pure function of the state, so the phone and the server always agree.
 */
import { HALF_CENT, centsToMicro, formatCents, formatMicro, type Micro } from "../money.ts";
import { receiptKey, tripKey, type GroupState, type IsoDate, type Item } from "../types.ts";
import { effectiveCategory, splitItem, type ItemSplit } from "./split.ts";

export type ShareStatus = "paid" | "partial" | "unpaid";
export type PersonStatus = "you" | "paid" | "owes" | "owed" | "none";
export type OverdueLevel = "none" | "warn" | "late";

export interface ComputedItem extends ItemSplit {
  item: Item;
  category: string;
  /** Status of each ticked person's share. */
  status: Record<string, ShareStatus>;
  /** What's still owed on each share, in micro (0 when paid). */
  remaining: Record<string, Micro>;
  /** Warnings for this row, in plain words. */
  checks: string[];
  /**
   * The same item number more than once on one receipt. Same price: you bought several (shown
   * as one row, ×count). Different prices: packages priced by weight, like chicken.
   */
  repeat?: { key: string; count: number; index: number; samePrice: boolean };
}

export interface UnpaidShare {
  itemId: string;
  name: string;
  date: IsoDate;
  remainingMicro: Micro;
  /** True when part of this share is already covered. */
  partial: boolean;
}

export interface PersonSummary {
  personId: string;
  name: string;
  color: string;
  /** Sum of this person's shares. */
  totalMicro: Micro;
  /** Items this person paid the store for (they get credit). */
  frontedMicro: Micro;
  /** Payments: positive = they paid you, negative = you paid them. */
  paidCents: number;
  /** total - fronted - paid. Positive = they owe you; negative = you owe them. */
  balanceMicro: Micro;
  status: PersonStatus;
  unpaid: UnpaidShare[];
  oldestUnpaid: IsoDate | null;
  daysOverdue: number;
  overdue: OverdueLevel;
  lastPaid: string | null;
}

export interface TripSummary {
  key: string;
  date: IsoDate;
  store: string;
  /** The receipt's label when a visit had several ("" = the only or main one). */
  receipt: string;
  /** The receipt's id (absent on receipts from before ids). */
  receiptId?: string;
  /** Cards or methods used on this receipt, most-used first. */
  cards: string[];
  itemCount: number;
  totalMicro: Micro;
  receiptCents: number | null;
  /** Sheet total minus printed receipt, when a receipt total is set. */
  diffMicro: Micro | null;
}

export interface CatalogEntry {
  itemNo: string;
  name: string;
  lastPriceCents: number;
  lastDate: IsoDate;
  store: string;
  timesBought: number;
}

export interface Breakdown {
  label: string;
  totalMicro: Micro;
}

export interface Ledger {
  /** Items in display order: by date, then the order they were added. */
  items: ComputedItem[];
  byItemId: Record<string, ComputedItem>;
  people: PersonSummary[];
  byPersonId: Record<string, PersonSummary>;
  trips: TripSummary[];
  catalog: CatalogEntry[];
  totals: {
    allMicro: Micro;
    assignedMicro: Micro;
    unassignedMicro: Micro;
    outstandingMicro: Micro;
  };
  insights: {
    byStore: Breakdown[];
    byCategory: Breakdown[];
    byMonth: Breakdown[];
    byPerson: Breakdown[];
    byCard: Breakdown[];
  };
}

// Reused collators: String.localeCompare sets one up on every call.
const byText = new Intl.Collator("en").compare;
const byNumberedText = new Intl.Collator("en", { numeric: true }).compare;

export function displayOrder(state: GroupState): Item[] {
  const index = new Map(state.itemOrder.map((id, i) => [id, i]));
  return state.itemOrder
    .map((id) => state.items[id])
    .filter((i): i is Item => i !== undefined)
    .sort((a, b) =>
      a.date < b.date ? -1 : a.date > b.date ? 1 : (index.get(a.id) ?? 0) - (index.get(b.id) ?? 0),
    );
}

export function computeLedger(state: GroupState, today: IsoDate): Ledger {
  const ordered = displayOrder(state);
  const items: ComputedItem[] = ordered.map((item) => ({
    ...splitItem(state, item),
    item,
    category: effectiveCategory(state, item),
    status: {},
    remaining: {},
    checks: [],
  }));
  const byItemId: Record<string, ComputedItem> = {};
  // Each row's receipt key, worked out once (it's used for trips, duplicate checks and totals).
  const keys: string[] = new Array(items.length);
  for (let i = 0; i < items.length; i++) {
    const c = items[i]!;
    byItemId[c.item.id] = c;
    keys[i] = receiptKey(c.item);
  }

  // ---- money each person has put in: payments plus items they paid the store for
  const paidCents: Record<string, number> = {};
  const lastPaid: Record<string, string> = {};
  for (const id of state.paymentOrder) {
    const p = state.payments[id];
    if (!p) continue;
    paidCents[p.personId] = (paidCents[p.personId] ?? 0) + p.cents;
    if (p.cents > 0 && (lastPaid[p.personId] ?? "") < p.at) lastPaid[p.personId] = p.at;
  }
  const fronted: Record<string, Micro> = {};
  for (const c of items) {
    if (c.item.paidBy) fronted[c.item.paidBy] = (fronted[c.item.paidBy] ?? 0) + c.totalMicro;
  }

  // ---- apply each person's credit to their shares, oldest first (the Sheet's rule).
  // One pass over the items (each share updates its person's running total) rather than one
  // pass per person, which matters with hundreds of people or thousands of rows.
  interface Acc {
    credit: Micro;
    running: Micro;
    totalMicro: Micro;
    unpaid: UnpaidShare[];
  }
  const acc = new Map<string, Acc>();
  for (const personId of state.personOrder) {
    if (!state.people[personId]) continue;
    acc.set(personId, {
      credit: centsToMicro(paidCents[personId] ?? 0) + (fronted[personId] ?? 0),
      running: 0,
      totalMicro: 0,
      unpaid: [],
    });
  }
  for (const c of items) {
    for (const personId in c.shares) {
      const a = acc.get(personId);
      if (!a) continue;
      const share = c.shares[personId]!;
      a.totalMicro += share;
      a.running += share;
      const over = a.running - a.credit;
      let status: ShareStatus;
      let remaining: Micro;
      if (over <= HALF_CENT) {
        status = "paid";
        remaining = 0;
      } else if (over < share - HALF_CENT) {
        status = "partial";
        remaining = over;
      } else {
        status = "unpaid";
        remaining = share;
      }
      c.status[personId] = status;
      c.remaining[personId] = remaining;
      if (status !== "paid") {
        a.unpaid.push({
          itemId: c.item.id,
          name: c.item.name,
          date: c.item.date,
          remainingMicro: remaining,
          partial: status === "partial",
        });
      }
    }
  }

  const people: PersonSummary[] = [];
  for (const personId of state.personOrder) {
    const person = state.people[personId];
    const a = acc.get(personId);
    if (!person || !a) continue;
    const { credit, totalMicro, unpaid } = a;
    const balanceMicro = totalMicro - credit;
    const isOwner = state.settings.ownerPersonId === personId;
    const status: PersonStatus = isOwner
      ? "you"
      : Math.abs(balanceMicro) <= HALF_CENT
        ? totalMicro > 0
          ? "paid"
          : "none"
        : balanceMicro > 0
          ? "owes"
          : "owed";
    const oldestUnpaid = isOwner ? null : (unpaid[0]?.date ?? null);
    const daysOverdue = oldestUnpaid ? Math.max(0, daysBetween(oldestUnpaid, today)) : 0;
    const overdue: OverdueLevel =
      !oldestUnpaid || status !== "owes"
        ? "none"
        : daysOverdue >= state.settings.overdueLateDays
          ? "late"
          : daysOverdue >= state.settings.overdueWarnDays
            ? "warn"
            : "none";
    people.push({
      personId,
      name: person.name,
      color: person.color,
      totalMicro,
      frontedMicro: fronted[personId] ?? 0,
      paidCents: paidCents[personId] ?? 0,
      balanceMicro,
      status,
      unpaid: isOwner ? [] : unpaid,
      oldestUnpaid,
      daysOverdue,
      overdue,
      lastPaid: lastPaid[personId] ?? null,
    });
  }
  const byPersonId = Object.fromEntries(people.map((p) => [p.personId, p]));

  // ---- trips: one per receipt (its id; before ids, date + store + label), checked against its
  // printed total
  const tripMap = new Map<string, TripSummary>();
  const cardCount = new Map<string, Map<string, number>>();
  for (let i = 0; i < items.length; i++) {
    const c = items[i]!;
    const key = keys[i]!;
    if (c.item.paidWith) {
      const m = cardCount.get(key) ?? new Map<string, number>();
      m.set(c.item.paidWith, (m.get(c.item.paidWith) ?? 0) + 1);
      cardCount.set(key, m);
    }
    const t = tripMap.get(key) ?? {
      key,
      date: c.item.date,
      store: c.item.store,
      receipt: c.item.receipt ?? "",
      ...(c.item.receiptId ? { receiptId: c.item.receiptId } : {}),
      cards: [],
      itemCount: 0,
      totalMicro: 0,
      receiptCents: null,
      diffMicro: null,
    };
    t.itemCount += 1;
    t.totalMicro += c.totalMicro;
    tripMap.set(key, t);
  }
  for (const t of tripMap.values()) {
    t.cards = [...(cardCount.get(t.key) ?? new Map<string, number>()).entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([card]) => card);
    // By id first; a printed total set by store, date and label (before ids) still counts.
    const r =
      state.receipts[t.key] ??
      (t.receiptId ? state.receipts[tripKey(t.date, t.store, t.receipt)] : undefined);
    if (r !== undefined) {
      t.receiptCents = r;
      t.diffMicro = t.totalMicro - centsToMicro(r);
    }
  }
  const trips = [...tripMap.values()].sort((a, b) =>
    a.date !== b.date
      ? a.date < b.date
        ? 1
        : -1
      : a.store !== b.store
        ? byText(a.store, b.store)
        : byNumberedText(a.receipt, b.receipt),
  );

  // ---- per-row checks and the catalog of item numbers
  const catalogMap = new Map<string, CatalogEntry>();
  const seenInTrip = new Map<string, number>();
  for (let i = 0; i < items.length; i++) {
    const no = items[i]!.item.itemNo;
    if (no) {
      const k = `${keys[i]}#${no}`;
      seenInTrip.set(k, (seenInTrip.get(k) ?? 0) + 1);
    }
  }
  // Repeats on one receipt: same price everywhere means several of the same thing.
  const repeats = new Map<
    string,
    { count: number; index: number; samePrice: boolean; price: number }
  >();
  for (let i = 0; i < items.length; i++) {
    const it = items[i]!.item;
    if (!it.itemNo) continue;
    const k = `${keys[i]}#${it.itemNo}`;
    if ((seenInTrip.get(k) ?? 0) < 2) continue;
    const net = it.priceCents + it.couponCents;
    const g = repeats.get(k);
    if (!g) repeats.set(k, { count: seenInTrip.get(k)!, index: 0, samePrice: true, price: net });
    else if (g.price !== net) g.samePrice = false;
  }
  for (let i = 0; i < items.length; i++) {
    const c = items[i]!;
    const it = c.item;
    const trip = tripMap.get(keys[i]!);
    if (trip && trip.diffMicro !== null && Math.abs(trip.diffMicro) > HALF_CENT) {
      c.checks.push("Rows don't add up to the printed receipt total (see Receipts).");
    }
    if (it.itemNo && (seenInTrip.get(`${keys[i]}#${it.itemNo}`) ?? 0) > 1) {
      const g = repeats.get(`${keys[i]}#${it.itemNo}`)!;
      g.index += 1;
      c.repeat = {
        key: `${keys[i]}#${it.itemNo}`,
        count: g.count,
        index: g.index,
        samePrice: g.samePrice,
      };
    }
    for (const e of c.errors) c.checks.push(e);
    if (it.ticked.length === 0) c.checks.push("Nobody is ticked yet.");
    if (it.itemNo) {
      const prev = catalogMap.get(it.itemNo);
      if (prev && prev.lastPriceCents !== it.priceCents && prev.lastDate < it.date) {
        c.checks.push(`Last time this was ${formatCents(prev.lastPriceCents)} (${prev.lastDate}).`);
      }
      catalogMap.set(it.itemNo, {
        itemNo: it.itemNo,
        name: it.name,
        lastPriceCents: it.priceCents,
        lastDate: it.date,
        store: it.store,
        timesBought: (prev?.timesBought ?? 0) + 1,
      });
    }
  }
  const catalog = [...catalogMap.values()].sort((a, b) => byText(a.name, b.name));

  // ---- totals and breakdowns
  let allMicro = 0;
  let unassignedMicro = 0;
  const add = (m: Map<string, number>, k: string, v: number) => m.set(k, (m.get(k) ?? 0) + v);
  const byStore = new Map<string, number>();
  const byCategory = new Map<string, number>();
  const byMonth = new Map<string, number>();
  const byCard = new Map<string, number>();
  for (const c of items) {
    allMicro += c.totalMicro;
    if (c.item.ticked.length === 0) unassignedMicro += c.totalMicro;
    if (c.category === "Balance") continue; // carried-over balances aren't spending
    add(byStore, c.item.store, c.totalMicro);
    add(byCategory, c.category, c.totalMicro);
    add(byMonth, c.item.date.slice(0, 7), c.totalMicro);
    add(byCard, c.item.paidWith ?? "Not set", c.totalMicro);
  }
  const sorted = (m: Map<string, number>): Breakdown[] =>
    [...m.entries()]
      .map(([label, totalMicro]) => ({ label, totalMicro }))
      .sort((a, b) => b.totalMicro - a.totalMicro);

  return {
    items,
    byItemId,
    people,
    byPersonId,
    trips,
    catalog,
    totals: {
      allMicro,
      assignedMicro: people.reduce((s, p) => s + p.totalMicro, 0),
      unassignedMicro,
      outstandingMicro: people
        .filter((p) => p.status === "owes")
        .reduce((s, p) => s + p.balanceMicro, 0),
    },
    insights: {
      byStore: sorted(byStore),
      byCategory: sorted(byCategory),
      byMonth: [...byMonth.entries()]
        .map(([label, totalMicro]) => ({ label, totalMicro }))
        .sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0)),
      byPerson: people
        .filter((p) => p.totalMicro > 0)
        .map((p) => ({ label: p.name, totalMicro: p.totalMicro }))
        .sort((a, b) => b.totalMicro - a.totalMicro),
      byCard: sorted(byCard),
    },
  };
}

/** Whole days from a to b (b - a), using calendar dates only. */
export function daysBetween(a: IsoDate, b: IsoDate): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** The text the People page offers to copy into a message or Zelle note. */
export function reminderMessage(summary: PersonSummary): string {
  if (summary.status === "owes") {
    const parts = summary.unpaid.map(
      (u) => `${u.name} ${formatMicro(u.remainingMicro)}${u.partial ? " (rest of it)" : ""}`,
    );
    return `Hi ${summary.name}! You owe me ${formatMicro(summary.balanceMicro)} for: ${parts.join(", ")}. Thanks!`;
  }
  if (summary.status === "owed") {
    return `Hi ${summary.name}! I owe you ${formatMicro(-summary.balanceMicro)}. Sending it now.`;
  }
  return "";
}
